import type { MarketingOpportunityItem } from '../types';

export type NormalizedOpportunityRadar = {
  opportunities: MarketingOpportunityItem[];
  otherSignals: MarketingOpportunityItem[];
  summary?: string;
  highlights: string[];
  hasContent: boolean;
};

const OPPORTUNITY_ARRAY_KEYS = [
  'opportunities',
  'radar_items',
  'items',
  'opportunity_signals',
  'opportunity_radar_items',
  'opportunity_list',
  'radar_opportunities',
  'identified_opportunities',
  'leads',
  'outreach_opportunities',
  'pursuits',
  'recommended_outreach',
  'action_items',
] as const;

const OTHER_SIGNAL_ARRAY_KEYS = [
  'excluded_signals',
  'other_signals',
  'passed_signals',
  'skipped_signals',
  'not_selected',
  'not_pursued',
  'reviewed_signals',
  'watchlist',
] as const;

const NESTED_PAYLOAD_KEYS = ['opportunity_radar', 'radar', 'payload', 'report'] as const;

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function parseStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string' && !!x.trim()).map(x => x.trim());
}

function firstNonEmptyString(...values: unknown[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function collectArrays(source: Record<string, unknown>, keys: readonly string[]): unknown[] {
  const out: unknown[] = [];
  for (const key of keys) {
    const raw = source[key];
    if (Array.isArray(raw) && raw.length) out.push(...raw);
  }
  return out;
}

/** Flatten one level of common agent nesting on an opportunity item. */
function flattenOpportunityItem(item: Record<string, unknown>): Record<string, unknown> {
  for (const nestKey of ['opportunity', 'details', 'outreach', 'lead', 'target']) {
    const nested = item[nestKey];
    if (isRecord(nested)) {
      return { ...nested, ...item };
    }
  }
  return item;
}

/** Prefer the first non-empty array; skip empty arrays so stale fields do not block reads. */
function firstOpportunityArray(source: Record<string, unknown>): unknown[] {
  let emptyFallback: unknown[] | null = null;
  for (const key of OPPORTUNITY_ARRAY_KEYS) {
    const raw = source[key];
    if (!Array.isArray(raw)) continue;
    if (raw.length) return raw;
    if (!emptyFallback) emptyFallback = raw;
  }
  return emptyFallback ?? [];
}

function signalsArray(source: Record<string, unknown>): unknown[] {
  return Array.isArray(source.signals) && source.signals.length ? source.signals : [];
}

/** Pull an opportunities array from varied agent / Firestore payload shapes. */
export function extractOpportunityArray(source: Record<string, unknown>): unknown[] {
  const direct = firstOpportunityArray(source);
  if (direct.length) return direct;

  for (const nestKey of NESTED_PAYLOAD_KEYS) {
    const nested = source[nestKey];
    if (isRecord(nested)) {
      const inner = extractOpportunityArray(nested);
      if (inner.length) return inner;
    }
  }

  const siblingSignals = signalsArray(source);
  if (siblingSignals.length) return siblingSignals;

  return direct;
}

function extractOtherSignalArray(
  source: Record<string, unknown>,
  hasSelectedOpportunities: boolean,
): unknown[] {
  const dedicated = collectArrays(source, OTHER_SIGNAL_ARRAY_KEYS);
  const fromNested = NESTED_PAYLOAD_KEYS.flatMap(nestKey => {
    const value = source[nestKey];
    return isRecord(value) ? collectArrays(value, OTHER_SIGNAL_ARRAY_KEYS) : [];
  });
  const combined = dedicated.concat(fromNested);
  if (combined.length > 0) return combined;

  if (!hasSelectedOpportunities) return [];

  const sibling = signalsArray(source);
  if (sibling.length > 0) return sibling;

  for (const nestKey of NESTED_PAYLOAD_KEYS) {
    const value = source[nestKey];
    if (!isRecord(value)) continue;
    const inner = signalsArray(value);
    if (inner.length > 0) return inner;
  }

  return [];
}

export function dedupeOpportunityItems(
  items: MarketingOpportunityItem[],
  against: MarketingOpportunityItem[] = [],
): MarketingOpportunityItem[] {
  const seen = new Set(against.map(item => item.title.trim().toLowerCase()).filter(Boolean));
  const out: MarketingOpportunityItem[] = [];
  for (const item of items) {
    const key = item.title.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/** True when payload is an opportunity radar report, not a competitor briefing. */
export function hasDedicatedRadarContent(source: Record<string, unknown>): boolean {
  return extractOpportunityArray(source).length > 0;
}

export function parseOpportunityItems(raw: unknown): MarketingOpportunityItem[] {
  const list = Array.isArray(raw) ? raw : extractOpportunityArray(isRecord(raw) ? raw : {});
  const out: MarketingOpportunityItem[] = [];

  for (const item of list) {
    if (typeof item === 'string' && item.trim()) {
      out.push({ title: item.trim() });
      continue;
    }
    if (!isRecord(item)) continue;

    const flat = flattenOpportunityItem(item);
    const title = firstNonEmptyString(
      flat.title,
      flat.opportunity,
      flat.name,
      flat.signal,
      flat.headline,
      flat.topic,
      flat.recommendation,
      flat.text,
      flat.subject,
      flat.opportunity_title,
      flat.company_name,
      flat.description,
      flat.summary,
      flat.note,
      flat.action_item,
      flat.who_to_contact,
      flat.outreach_target,
      flat.target,
      flat.address,
      flat.location,
      flat.property,
      flat.property_name,
      flat.listing,
      flat.listing_title,
    );
    if (!title) continue;

    out.push({
      ...flat,
      title,
      category:
        typeof flat.category === 'string'
          ? flat.category
          : typeof flat.type === 'string'
            ? flat.type
            : undefined,
      rationale:
        firstNonEmptyString(flat.rationale, flat.reason, flat.why) ||
        (typeof flat.description === 'string' && flat.description.trim() !== title
          ? flat.description.trim()
          : undefined) ||
        undefined,
      urgency: firstNonEmptyString(flat.urgency, flat.priority) || undefined,
      suggested_action:
        firstNonEmptyString(
          flat.suggested_action,
          flat.suggestedAction,
          flat.action,
          flat.outreach_action,
          flat.next_step,
        ) || undefined,
      skip_reason:
        firstNonEmptyString(
          flat.skip_reason,
          flat.why_not,
          flat.skipReason,
          flat.exclusion_reason,
          flat.excluded_reason,
          flat.why_excluded,
        ) || undefined,
    });
  }

  return out;
}

function parseOtherSignals(
  source: Record<string, unknown>,
  opportunities: MarketingOpportunityItem[],
): MarketingOpportunityItem[] {
  return dedupeOpportunityItems(
    parseOpportunityItems(extractOtherSignalArray(source, opportunities.length > 0)),
    opportunities,
  );
}

export function normalizeOpportunityRadarPayload(
  source: Record<string, unknown>,
): NormalizedOpportunityRadar {
  // Firestore docs from upsertOpportunityReport: trust top-level opportunities first.
  const stored = parseOpportunityItems(source.opportunities);
  if (stored.length) {
    return {
      opportunities: stored,
      otherSignals: parseOtherSignals(source, stored),
      summary: typeof source.summary === 'string' ? source.summary : undefined,
      highlights: parseStringArray(source.highlights),
      hasContent: true,
    };
  }

  // Merge nested agent payloads — top-level fields win over nested (avoids stale radar:{} clobber).
  let root = source;
  for (const nestKey of NESTED_PAYLOAD_KEYS) {
    const nested = source[nestKey];
    if (isRecord(nested)) {
      root = { ...nested, ...source };
      break;
    }
  }

  const opportunities = parseOpportunityItems(extractOpportunityArray(root));
  const highlights = parseStringArray(root.highlights);
  const summary = typeof root.summary === 'string' ? root.summary : undefined;

  return {
    opportunities,
    otherSignals: parseOtherSignals(root, opportunities),
    summary,
    highlights,
    hasContent: opportunities.length > 0,
  };
}

export function buildOpportunityHeadline(
  data: Record<string, unknown>,
  opportunities: MarketingOpportunityItem[],
): string {
  const highlights = parseStringArray(data.highlights);
  if (highlights[0]) return highlights[0];
  if (typeof data.summary === 'string' && data.summary.trim()) {
    return data.summary.trim().slice(0, 120);
  }
  if (opportunities[0]?.title) return opportunities[0].title;
  return '';
}
